import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Heart, Award, Book, Globe, ArrowRight, Quote, Leaf } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const KholeyPage = () => {
  usePageTitle('Romona Nicole Schmidt - Guest Educator');
  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero Section */}
      <div className="relative py-24 px-6 bg-[#1e1919] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 30%, #9D4EDD 0%, transparent 50%),
                             radial-gradient(circle at 70% 70%, #c9a96e 0%, transparent 50%)`
          }} />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#c9a96e]" />
                <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium">
                  Guest Educator
                </span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
                Romona Nicole Schmidt
              </h1>
              <p className="text-xl text-[#c9a96e] mb-6 font-medium">
                Gemstone & Natural Healing Authority
              </p>
              <p className="text-lg text-[#bbb5ae] leading-relaxed mb-8">
                Bridging Ancient Gemstone Lore and Modern Holistic Practice
              </p>
              <Link
                to="/webinar-registration"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-[0_8px_25px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-105"
                data-testid="register-webinar-btn"
              >
                Register for Live Webinar
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              {/* Floating crystals animation */}
              <div className="absolute -top-4 -left-4 w-8 h-8 animate-float-slow z-20">
                <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                  <path d="M12 2L15 10H9L12 2Z" fill="#9D4EDD" fillOpacity="0.8"/>
                  <path d="M12 22L9 14H15L12 22Z" fill="#9D4EDD" fillOpacity="0.6"/>
                  <path d="M12 2L12 22" stroke="#9D4EDD" strokeWidth="0.5" strokeOpacity="0.5"/>
                </svg>
              </div>
              <div className="absolute top-1/4 -right-6 w-10 h-10 animate-float-medium z-20">
                <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                  <polygon points="12,2 22,12 12,22 2,12" fill="#FF6B9D" fillOpacity="0.7"/>
                </svg>
              </div>
              <div className="absolute bottom-1/4 -left-8 w-6 h-6 animate-float-fast z-20">
                <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                  <path d="M12 2L14 8H10L12 2ZM12 22L10 16H14L12 22ZM2 12L8 10V14L2 12ZM22 12L16 14V10L22 12Z" fill="#c9a96e" fillOpacity="0.8"/>
                </svg>
              </div>
              <div className="absolute -bottom-2 right-1/4 w-7 h-7 animate-float-slow z-20" style={{ animationDelay: '1s' }}>
                <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                  <path d="M12 2L20 12L12 22L4 12L12 2Z" fill="#00B4D8" fillOpacity="0.6"/>
                </svg>
              </div>
              
              {/* Sparkle particles around image */}
              <div className="absolute inset-0 pointer-events-none z-10">
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-full animate-pulse"
                    style={{
                      left: `${10 + Math.random() * 80}%`,
                      top: `${10 + Math.random() * 80}%`,
                      background: ['#9D4EDD', '#FF6B9D', '#c9a96e', '#00B4D8'][i % 4],
                      animationDelay: `${i * 0.3}s`,
                      animationDuration: `${2 + Math.random()}s`
                    }}
                  />
                ))}
              </div>
              
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-[#9D4EDD]/20 to-[#c9a96e]/20 relative">
                {/* Glowing border effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#9D4EDD] via-[#FF6B9D] to-[#c9a96e] p-[3px] animate-pulse-soft">
                  <div className="w-full h-full rounded-2xl overflow-hidden bg-white">
                    <img
                      src="https://customer-assets.emergentagent.com/job_12c5979f-07d4-4fbe-88fc-c2d666777007/artifacts/3xo7hul1_Facetune_16-11-2024-19-08-45.jpeg"
                      alt="Romona Nicole Schmidt"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 glass-effect px-6 py-4 rounded-xl shadow-lg bg-white/90">
                <p className="text-3xl font-serif text-[#9D4EDD]">10+</p>
                <p className="text-xs uppercase tracking-wider text-[#61525a]">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bio Section */}
      <div className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative mb-12">
            <Quote className="w-12 h-12 text-[#9D4EDD]/20 absolute -top-4 -left-4" />
            <blockquote className="text-2xl md:text-3xl font-serif text-[#1e1919] leading-relaxed pl-8">
              "My work is about empowerment through knowledge. For centuries, cultures worldwide have 
              understood the symbiotic relationship between humans and the earth's minerals and plants. 
              I'm simply helping people rediscover that language in a practical, accessible way."
            </blockquote>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-[#61525a] leading-relaxed text-lg mb-6">
              Romona Nicole Schmidt, based in Sedona, Arizona, is a recognized authority in the field of 
              vibrational wellness and natural healing. She brings a deeply intuitive and scholarly approach 
              to the use of gemstones and botanicals, with a background steeped in the study of mineralogical 
              properties and herbal traditions.
            </p>
            <p className="text-[#61525a] leading-relaxed text-lg mb-6">
              Schmidt has developed a dedicated following for her ability to demystify esoteric practices 
              for a modern audience. Her methodology moves beyond aesthetic appreciation, focusing on the 
              documented historical use and energetic principles of crystals and herbs.
            </p>
            <p className="text-[#61525a] leading-relaxed text-lg">
              She emphasizes a place of intentionality, where individuals are guided to create personalized 
              wellness protocols that incorporate these natural elements into daily life.
            </p>
          </div>
        </div>
      </div>

      {/* Expertise Areas */}
      <div className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-[#c9a96e] font-medium mb-4 block">
              Areas of Expertise
            </span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1e1919]">
              Specialized Knowledge
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Heart,
                title: 'Vibrational Wellness',
                description: 'Understanding the energetic frequencies of crystals and their interaction with the human energy field.',
                color: '#FF6B9D'
              },
              {
                icon: Book,
                title: 'Mineralogical Studies',
                description: 'From grounding properties of specific basalts to clarifying frequencies of quartz families.',
                color: '#9D4EDD'
              },
              {
                icon: Leaf,
                title: 'Herbal Traditions',
                description: 'Correlating crystal properties with complementary herbal allies for integrated wellness protocols.',
                color: '#00C853'
              },
              {
                icon: Globe,
                title: 'Ethical Sourcing',
                description: 'Emphasizing a deep, respectful relationship with natural materials and responsible sourcing practices.',
                color: '#00B4D8'
              },
              {
                icon: Award,
                title: 'Personalized Protocols',
                description: 'Guiding individuals to create customized wellness routines incorporating crystals and herbs.',
                color: '#FFB800'
              },
              {
                icon: Sparkles,
                title: 'Intentional Practice',
                description: '"It\'s not just about what a stone can do for you; it\'s about entering into a respectful relationship with it."',
                color: '#9D4EDD'
              }
            ].map((item, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl bg-[#faf8f5] hover:shadow-lg transition-all duration-300 group"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color}20 0%, ${item.color}10 100%)`,
                    border: `2px solid ${item.color}30`
                  }}
                >
                  <item.icon className="w-6 h-6" style={{ color: item.color }} />
                </div>
                <h3 className="font-serif text-xl text-[#1e1919] mb-3">{item.title}</h3>
                <p className="text-[#61525a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 px-6 bg-gradient-to-br from-[#9D4EDD]/10 via-[#faf8f5] to-[#c9a96e]/10">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="w-8 h-8 text-[#c9a96e] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl text-[#1e1919] mb-6">
            Learn Directly from Romona Nicole Schmidt
          </h2>
          <p className="text-lg text-[#61525a] mb-8 max-w-2xl mx-auto">
            Join our exclusive live webinar on <strong>Selenite Healing Mastery</strong> in July 2026. 
            Limited spots available for this transformative learning experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/webinar-registration"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white px-8 py-4 rounded-xl font-semibold hover:shadow-[0_8px_25px_rgba(157,78,221,0.4)] transition-all duration-300 hover:scale-105"
              data-testid="cta-register-btn"
            >
              Register Now - $800
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/shop?category=selenite"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#9D4EDD] text-[#9D4EDD] px-8 py-4 rounded-xl font-semibold hover:bg-[#9D4EDD] hover:text-white transition-all duration-300"
              data-testid="shop-selenite-btn"
            >
              Shop Selenite Crystals
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KholeyPage;
